/**
 * `<saloon-landing>`: the front room - the title, and a menu in the corner
 * for dealing a game, seeing friends, and changing who you are.
 *
 * The new-game and profile dialogs belong to the app, so those items only ask
 * for them. Friends opens here: the pane's requests are composed, so they
 * still travel out to the app from inside this dialog.
 */

import type { StoredFriend } from "../db.js";
import { h } from "../dom.js";
import { emit } from "../events.js";
import { BaseElement } from "./base-element.js";
import styles from "./saloon-landing.css";
import "./friends-pane.js";

export class SaloonLanding extends BaseElement {
    readonly #friends = document.createElement("friends-pane");

    readonly #friendsDialog = h("dialog", { "class": "friends", "aria-label": "Friends" });

    constructor() {
        super(styles);

        const newGame = h("button", { type: "button", class: "primary" }, "New Game");
        const friends = h("button", { type: "button" }, "Friends");
        const settings = h("button", { type: "button" }, "Settings");
        const close = h("button", { type: "button", class: "quiet" }, "Close");

        newGame.addEventListener("click", () => emit(this, "play-requested", null));
        friends.addEventListener("click", () => this.#friendsDialog.showModal());
        settings.addEventListener("click", () => emit(this, "edit-profile", null));
        close.addEventListener("click", () => this.#friendsDialog.close());

        this.#friendsDialog.append(this.#friends, h("div", { class: "actions" }, close));

        this.root.append(
            h(
                "header",
                { class: "masthead" },
                h("h1", {}, "Hand and Foot", h("span", { class: "sub" }, "Saloon"))
            ),
            h(
                "nav",
                { "class": "menu", "aria-label": "Main menu" },
                h("ul", {}, h("li", {}, newGame), h("li", {}, friends), h("li", {}, settings))
            ),
            this.#friendsDialog
        );
    }

    set friends(value: readonly StoredFriend[]) {
        this.#friends.friends = value;
    }

    protected render(): void {
        // Everything is built once; the friends list renders itself.
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "saloon-landing": SaloonLanding;
    }
}

customElements.define("saloon-landing", SaloonLanding);
